import React, {RefObject} from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {
    KatButton,
    KatLabel,
    KatLink,
    KatPopover,
} from "@amzn/katal-react";
import MetricsUtil from "src/utils/MetricsUtil";
import {getCurrencyUnitConfig, getProgramLink} from "src/config/Config";
import {getCurrencyString} from "../../utils/ProgramDataUtil"
import CurrencyItem from "src/model/CurrencyItem";
import NumberKatInput from "../common/NumberKatInput";
import QueryProductInfo from "src/model/QueryProduct";
import {bindActionCreators, Dispatch} from "redux";
import {connect} from "react-redux";
import {setUpdateProgramFeesQueryStart} from "src/actions/ProgramFeesQueryAction";
import {setUpdateNewProgramFeesQueryStart} from "src/actions/NewProgramFeesQueryAction";
import FormatUtil from "src/utils/FormatUtil";
import {ProgramMetadata} from "src/model/ProgramMetadata";
import {RecommendationInfo} from "src/model/Recommendation";

export interface ProgramProfitProps {
    selectedProgram: string;
    isMFN: boolean;
    salesPrice: CurrencyItem;
    shippingCharge: CurrencyItem;
    costPerUnit: CurrencyItem;
    countryCode: string;
    queryProduct: QueryProductInfo;
    programMetadata?: ProgramMetadata;
    recommendationInfo?: RecommendationInfo;
}

interface OwnProps {
    readonly metricsUtil?: MetricsUtil;
    readonly firstRef?: RefObject<any>;
}

interface DispatchProps {
    readonly dispatchUpdateProgramFees: (countryCode: string, queryProduct: QueryProductInfo, programIdList: Array<string>) => void,
    readonly dispatchUpdateNewProgramFees: (countryCode: string, queryProduct: QueryProductInfo, programIdList: Array<string>) => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchUpdateProgramFees: setUpdateProgramFeesQueryStart,
        dispatchUpdateNewProgramFees: setUpdateNewProgramFeesQueryStart
    }, dispatch)
);

const connector = connect(null, mapDispatchToProps);

type Props = ProgramProfitProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    estimatedSales: number;
}

interface CalculationResult {
    profitPerUnit: CurrencyItem; // = salesPrice + shippingCharge - costPerUnit
    netProfit: CurrencyItem; // profitPerUnit * estimatedSales
    netMarginPercentage: number; // netProfit / total revenue
}

export class ProgramProfitComponent extends React.Component<Props, State> {
    private readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);

        this.state = {
            estimatedSales: 1, // input
        };

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }
    }

    private isDataValid = () => {
        if (this.props.costPerUnit == null
            || !this.props.countryCode || this.props.countryCode == null
            || this.props.queryProduct == null) {
            if (this.metricsUtil) {
                this.metricsUtil.publishCounter("programProfitData", "error");
            }
            return false;
        }
        return true;
    }

    private handleHelpLinkClick = (selectedProgram: string) => {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(selectedProgram, "helpLink");
        }
    }

    private handleRecommendationClick = (selectedProgram: string, recommendationLink: string) => {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(selectedProgram, "RecommendationLink");
        }
        window.open(recommendationLink, "_blank");
    }

    // re-calculate {profitPerUnit, netProfit}
    reCalculate(): CalculationResult {
        const currencyUnit = this.props.costPerUnit.currency;
        const revenuePerUnitAmount = this.props.salesPrice.amount + (this.props.isMFN ? this.props.shippingCharge.amount : 0);
        const profitPerUnitAmount = revenuePerUnitAmount - this.props.costPerUnit.amount;
        const netProfitAmount = profitPerUnitAmount * this.state.estimatedSales;
        const netMarginPercentage = profitPerUnitAmount / (revenuePerUnitAmount <= 0 ? 1 : revenuePerUnitAmount);
        return {
            profitPerUnit: {amount: profitPerUnitAmount, currency: currencyUnit},
            netProfit: {amount: netProfitAmount, currency: currencyUnit},
            netMarginPercentage: netMarginPercentage,
        }
    }

    handleEstimatedSalesChange(input: number) {
        this.setState({estimatedSales: input});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("estimatedSales", "input");
        }
    }

    generateLabelWithOptionalPopover(labelStringId: string, popoverStringId?: string, labelClass?: string) {
        const {t} = this.props;
        if (popoverStringId) {
            return (
                <KatPopover triggerType="hover" katAriaBehavior="tooltip" position="bottom-start">
                    <KatLabel slot="trigger" text={t(labelStringId)} className={labelClass ? labelClass : "input-block-text"}/>
                    <KatLabel text={t(popoverStringId)} className="popover-content"/>
                </KatPopover>
            );
        } else {
            return (
                <KatLabel text={t(labelStringId)} className={labelClass ? labelClass : "input-block-text"}/>
            );
        }
    }

    getLinkContent() {
        const {t, recommendationInfo} = this.props;
        if (this.props.programMetadata
            && this.props.programMetadata.name
            && this.props.countryCode) {
            const link = getProgramLink(this.props.programMetadata.name, this.props.countryCode)
            if (link != "") {
                if (recommendationInfo) {
                    return (
                        <div className="subsection-content-align-central">
                            <KatButton variant="secondary" label={t('SC_FBA_Calc_btn_createFBAShipment')} className="recommendation-link"
                                       onClick={(event) => {
                                           event.preventDefault();
                                           this.handleRecommendationClick(this.props.selectedProgram, link);
                                       }}/>
                        </div>
                    );
                }
                return (
                    <div className="subsection-content-align-central">
                        <KatLink label={t('SC_FBA_Calc_link_program')}
                                 href={link} variant="link" target="_blank"
                                 className="learn-more-program-link" onClick={() => this.handleHelpLinkClick(this.props.selectedProgram)}/>
                    </div>
                )
            }
        }
        return (<></>)
    }

    render() {
        const calculateResult = this.reCalculate();

        if (!this.isDataValid()) {
            return (<></>);
        }

        const estimatedSalesContent = (
            <div className="input-block input-block-padding">
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_label_estimatedSales")}
                <NumberKatInput isDisabled={false} className="input-block-inputbox" minValue={0} isFloat={false} ref={this.props.firstRef}
                                value={this.state.estimatedSales != 0 ? this.state.estimatedSales : undefined}
                                onInput={(input: number, hasError: boolean): void => {
                                    if (!hasError) {
                                        this.handleEstimatedSalesChange(input);
                                    }
                                }}/>
            </div>
        );

        const costPerUnitContent = (
            <div className="input-block input-block-padding-plus">
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_label_estimatedCost")}
                <KatLabel text={getCurrencyString(this.props.costPerUnit)} className="subsection-content-currency"/>
            </div>
        );

        const netProfitPerUnitContent = (
            <div className="input-block input-block-padding-plus">
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_label_netProfitPerUnit")}
                <KatLabel text={getCurrencyString(calculateResult.profitPerUnit)} className="subsection-content-currency"/>
            </div>
        );

        const netProfitContent = (
            <div className="input-block input-block-padding-plus">
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_netProfit_new", "", "input-block-text-bold")}
                <KatLabel text={getCurrencyString(calculateResult.netProfit)}
                          className={calculateResult.netProfit.amount >= 0 ? "net-profit-currency-positive" : "net-profit-currency-negative"}/>
            </div>
        );

        const netMarginPercentageContent = (
            <div className="input-block input-block-padding-plus">
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_netMargin_new", "", "input-block-text-bold")}
                <KatLabel text={FormatUtil.formatPercentage(calculateResult.netMarginPercentage, 2)}
                          className={calculateResult.netMarginPercentage >= 0 ? "net-profit-currency-positive" : "net-profit-currency-negative"}/>
            </div>
        );

        return (
            <div className="profit-section">
                <div className="section-content">
                    {costPerUnitContent}
                    {netProfitPerUnitContent}
                    {estimatedSalesContent}
                </div>
                <div className="net-profit">
                    {netProfitContent}
                    {netMarginPercentageContent}
                </div>
                {this.getLinkContent()}
            </div>
        );
    }
}

export default withTranslation()(connector(ProgramProfitComponent));
